import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiUrl } from "../config";
import { UserAuth } from "../contexts/AuthContext";

const displayName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en || name.fr || "";
};

const ClaimBusiness = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const { signInWithEmail } = UserAuth();

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [business, setBusiness] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!businessId || !token) {
        setVerifyError("This link is missing required information.");
        setVerifying(false);
        return;
      }
      try {
        const res = await axios.get(
          `${apiUrl}/verify-owner-claim/${businessId}`,
          { params: { token } },
        );
        setBusiness(res.data.business);
        // Pre-fill first name from the contact name on file when present.
        const contact = (res.data.business?.contactName || "").trim();
        if (contact) {
          const [first, ...rest] = contact.split(/\s+/);
          setFirstName(first);
          if (rest.length) setLastName(rest.join(" "));
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          "This link is invalid or has expired.";
        setVerifyError(msg);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [businessId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || password.length < 6) {
      toast({
        title: "Please complete all fields",
        description:
          "First name, last name, and a password of 6+ characters are required.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${apiUrl}/claim-business-ownership`, {
        businessId,
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      // Sign the new owner in so the BusinessDashboard renders authenticated.
      await signInWithEmail(business.email, password);
      toast({
        title: "Account created",
        description: "You're now the owner of this business.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      navigate(`/business/${businessId}/dashboard`);
    } catch (err) {
      const code = err?.response?.data?.error;
      let description = "Could not create your account. Please try again.";
      if (code === "EMAIL_IN_USE") {
        description =
          "An account already exists for this email. Please sign in and contact support to link this business.";
      } else if (code === "PASSWORD_REQUIRED") {
        description = "A password is required.";
      } else if (err?.response?.data?.error) {
        description = err.response.data.error;
      }
      toast({
        title: "Could not complete registration",
        description,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <Flex minH="100vh" justify="center" align="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (verifyError) {
    return (
      <Flex minH="100vh" justify="center" align="center" px={4}>
        <Box maxW="md" w="100%">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {verifyError}
          </Alert>
          <Text mt={4} fontSize="sm" color="gray.600">
            If you believe this is a mistake, contact the campaign that invited
            you and ask them to resend the activation link.
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" justify="center" align="center" px={4} py={10}>
      <Box maxW="md" w="100%" bg="white" p={8} borderRadius="xl" boxShadow="lg">
        <VStack align="stretch" spacing={4}>
          <Heading size="md">Activate your business account</Heading>
          <Text fontSize="sm" color="gray.600">
            Create your owner login for{" "}
            <strong>{displayName(business?.name) || "your business"}</strong>.
            You'll be signed in to your dashboard right after.
          </Text>
          <Text fontSize="sm" color="gray.500">
            Email: <strong>{business?.email}</strong>
          </Text>
          <form onSubmit={handleSubmit}>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">First name</FormLabel>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Last name</FormLabel>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword((v) => !v)}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={submitting}
                loadingText="Creating account…"
              >
                Create account
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ClaimBusiness;
